<script setup lang="ts">
import { ref } from 'vue';

const props = withDefaults(
  defineProps<{
    title: string;
    subtitle?: string;
    icon?: string;
    defaultOpen?: boolean;
    sectionKey: string;
  }>(),
  {
    subtitle: undefined,
    icon: undefined,
    defaultOpen: false,
  },
);

const isOpen = ref(props.defaultOpen);

function toggle() {
  isOpen.value = !isOpen.value;
}

defineExpose({ isOpen });
</script>

<template>
  <section
    :id="`section-${sectionKey}`"
    class="cs__section"
    :class="{ 'cs__section--open': isOpen }"
  >
    <button
      type="button"
      class="cs__hd"
      :aria-expanded="isOpen"
      @click="toggle"
    >
      <div v-if="icon" class="epp__section-ico">
        <i class="pi" :class="icon" />
      </div>
      <div class="cs__hd-text">
        <p class="epp__section-title">{{ title }}</p>
        <p v-if="subtitle" class="epp__section-sub">{{ subtitle }}</p>
      </div>
      <i
        class="pi pi-chevron-down cs__chevron"
        :class="{ 'cs__chevron--open': isOpen }"
      />
    </button>
    <div class="cs__collapse" :class="{ 'cs__collapse--open': isOpen }">
      <div class="cs__collapse-inner">
        <slot />
      </div>
    </div>
  </section>
</template>

<style scoped>
.cs__section {
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.cs__hd {
  display: flex;
  align-items: flex-start;
  gap: 0.875rem;
  width: 100%;
  background: transparent;
  border: none;
  padding: 0;
  margin: 0;
  cursor: pointer;
  text-align: left;
  font: inherit;
}

.epp__section-ico {
  width: 2.25rem;
  height: 2.25rem;
  border-radius: 0.625rem;
  background: var(--nxt-surface-low);
  border: 1px solid var(--nxt-outline-variant);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.9375rem;
  color: var(--nxt-primary);
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.cs__hd-text {
  flex: 1;
  min-width: 0;
}

.epp__section-title {
  margin: 0 0 0.125rem;
  font-size: 0.9375rem;
  font-weight: 700;
  color: var(--nxt-on-surface);
}

.epp__section-sub {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--nxt-on-surface-muted);
}

.cs__chevron {
  flex-shrink: 0;
  font-size: 0.875rem;
  color: var(--nxt-on-surface-muted);
  margin-top: 0.3rem;
  transition: transform 0.2s ease;
}
.cs__chevron--open {
  transform: rotate(180deg);
}

.cs__collapse {
  display: grid;
  grid-template-rows: 0fr;
  transition: grid-template-rows 0.25s ease;
}
.cs__collapse--open {
  grid-template-rows: 1fr;
}
.cs__collapse-inner {
  overflow: hidden;
}
.cs__collapse--open .cs__collapse-inner {
  padding-top: 1.125rem;
}

[data-theme='dark'] .epp__section-ico {
  background: rgba(255, 255, 255, 0.06);
  border-color: rgba(255, 255, 255, 0.08);
  color: #ffd60a;
}
</style>
